/* eslint-disable @nx/enforce-module-boundaries */
import {
  Avatar,
  Box,
  Divider,
  HStack,
  Heading,
  Link,
  LinkButton,
  SeeMore,
  Stack,
  Text,
  Button,
  Tooltip,
  IconButton,
} from '@mybridge/ui';
import moment from 'moment';
import { forwardRef, useContext, useEffect, useState, useRef } from 'react';
import { EMPLOYMENT_CHOICES } from 'v4/lib/data';
import { MyProfileContext } from '../context';
import { ShowAllLink } from '../show-all-link';
import {
  EarthGlobeIcon,
  TeamIcon,
  OnlyMeLockIcon,
  AddCircle,
  MilestoneIcon,
  AddCircleFIlled,
  NewPlusIcon
} from '@mybridge/icons';
import { EditIcon } from '@mybridge/icons/Edit';
import { isMobile } from 'react-device-detect';
import { MyProfileMilestoneModal } from '../milestones-modal';
import { AddIcon } from '@chakra-ui/icons';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useRouter } from 'next/router';

export const MyProfileMilestone = forwardRef(({ ...props }, ref) => {
  const {
    userProfileInfo,
    milestonesDisc,
    myMilestones,
    refetchMyMilestones,
    setSelectedMilestone,
    audienceDisc,
    setPermissionValue,
    setPermissionId,
    setCurrentPermissionValue,
  } = useContext(MyProfileContext);
  const { t } = useTranslator();
  const [showAll, setShowAll] = useState(false);
  const router = useRouter();
  const sectionRef = useRef(null);

  useEffect(() => {
    refetchMyMilestones?.();
  }, []);

  useEffect(() => {
    if (router.query.openDisc === 'milestones') {
      setSelectedMilestone(null);
      milestonesDisc?.onOpen?.();
      
      router.replace(router.pathname, undefined, { shallow: true });
    }
    
    if (router.query.scrollTo === 'milestones' && sectionRef.current) {
      const elementRect = sectionRef.current.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
      
      window.scrollTo({
        top: middle,
        behavior: 'smooth'
      });
      
      const { scrollTo, ...rest } = router.query;
      router.replace(
        { pathname: router.pathname, query: rest },
        undefined,
        { shallow: true }
      );
    }
  }, [router.query, milestonesDisc, setSelectedMilestone]);

  const onEditClick = (ms) => (e) => {
    setSelectedMilestone(ms);
    milestonesDisc?.onOpen?.();
  };

  const entityHasMY = (ent) => ent?.month && ent?.year;

  const renderPermissionSwitch = (param) => {
    switch (param) {
      case 1:
        return {
          icon: <EarthGlobeIcon width="16px" height="16px" />,
          text: t('profileDetails.public') || 'Public',
        };
      case 2:
        return {
          icon: <TeamIcon width="16px" height="16px" />,
          text: t('profileDetails.myNetwork') || 'myNetwork',
        };
      case 3:
        return {
          icon: <OnlyMeLockIcon width="16px" height="16px" />,
          text: t('profileDetails.onlyMe') || 'Only me',
        };
      default:
        return {
          icon: <EarthGlobeIcon width="16px" height="16px" />,
          text: t('profileDetails.public') || 'Public',
        };
    }
  };
  return (
    <>
      {milestonesDisc?.isOpen ? (
        <MyProfileMilestoneModal />
      ) : (
        <Stack ref={sectionRef}>
          <HStack alignItems="flex-start">
            <Stack flex={1}>
              <Heading
                fontSize="18px"
                color="brandPrimary.500"
                fontWeight="400"
              >
                {t('profileMilestones.milestones') || 'Milestones'}
              </Heading>
            </Stack>
            <HStack justifyContent="flex-end">
              {myMilestones?.length && (
                <IconButton
                  icon={<AddIcon color="#868585" />}
                  variant="transparent"
                  color="brandPrimary.500"
                  size={['sm', 'md']}
                  onClick={(e) => milestonesDisc?.onOpen?.()}
                />
              )}
            </HStack>
          </HStack>
          {/* <pre>{JSON.stringify(myMilestone, null, 2)}</pre> */}
          {myMilestones?.length ? (
            <>
              <Stack pt={4} spacing={6}>
                {myMilestones
                  ?.slice() // Create a copy to avoid mutating the original array
                  ?.sort((a, b) => {
                    // Sort by year first
                    if (b?.year !== a?.year) {
                      return b?.year - a?.year;
                    }
                    // If years are equal, sort by month
                    return b?.month - a?.month;
                  })
                  ?.filter?.((_, ind) => showAll || ind < 3)
                  ?.map?.((ms, msIndex) => (
                    <Stack key={msIndex}>
                      <HStack alignItems="flex-start">
                        <MilestoneIcon />
                        <Stack spacing={0} flex={1}>
                          <Box>
                            <Heading size="sm" fontWeight="400" sx={{ textTransform: 'capitalize' }}>
                              {ms?.title}
                            </Heading>
                          </Box>
                          {entityHasMY(ms) && (
                            <Box fontSize="sm">
                              <Text display="inline">
                                {moment()
                                  .set('M', parseInt(ms?.month - 1 ?? '0'))
                                  ?.format?.('MMMM')}{' '}
                                {moment().set('Y', ms?.year)?.format?.('YYYY')}
                              </Text>
                            </Box>
                          )}
                          <Stack py={2}>
                            {ms?.milestone && <SeeMore text={ms?.milestone} />}
                          </Stack>
                        </Stack>
                        <Stack>
                          <HStack>
                            <Tooltip
                              label={renderPermissionSwitch(ms?.audience)?.text}
                            >
                              <IconButton
                                icon={
                                  renderPermissionSwitch(ms?.audience)?.icon
                                }
                                size="sm"
                                variant="transparent"
                                onClick={() => {
                                  audienceDisc.onOpen();
                                  setPermissionValue('milestone');
                                  setCurrentPermissionValue(ms?.audience);
                                  setPermissionId(ms?.id);
                                }}
                              />
                            </Tooltip>
                            <Tooltip label={t('buttons.edit') || "Edit"}>
                              <IconButton
                                icon={<EditIcon width="16px" height="16px" />}
                                size="sm"
                                variant="transparent"
                                onClick={onEditClick(ms)}
                              />
                            </Tooltip>
                          </HStack>
                        </Stack>
                      </HStack>
                      {myMilestones?.length > 1 &&
                        msIndex < myMilestones?.length - 1 && <Divider />}
                    </Stack>
                  ))}
              </Stack>

              {!showAll && myMilestones?.length > 2 ? (
                <ShowAllLink onClick={(e) => setShowAll(true)} />
              ) : (
                ''
              )}
            </>
          ) : (
            <>
              <HStack justifyContent="flex-start">
             { isMobile ?
               (<> <Button
                  leftIcon={null} // Remove the default positioning of the icon
                  width="100%" // Adjust to desired width; fit-content will limit its flexibility
                  minW={isMobile&&"93vw"}
                  justifyContent="space-between" // Ensure icon moves to the end
                  variant="transparent"
                  color={isMobile ? '#747474' : 'brandPrimary.500'}
                  onClick={(e) => milestonesDisc?.onOpen?.()}
                  pl={0}
                >
                  <Box opacity={0.5}>{t('profileMilestones.addMilestone') || 'Add milestone'}</Box>
                  <NewPlusIcon width="20" height="20" />
                </Button>{' '}</>)
                :
                (<><Button
                  leftIcon={<AddCircleFIlled />}
                  variant="transparent"
                  color="brandPrimary.500"
                  onClick={(e) => milestonesDisc?.onOpen?.()}
                  pl={0}
                >
                  <Box opacity={0.5}>

                    {t('profileMilestones.addMilestone') || 'Add milestone'}
                  </Box>
                </Button></>)}
              </HStack>
            </>
          )}
        </Stack>
      )}
    </>
  );
});
